import React, {useContext, useEffect, useRef} from 'react';
import bryntumContext from "../context/bryntumContext.jsx";
import useRefsInit from "../hooks/useRefsInit.jsx";
import { assignViewsState, callOnLoadOnColumns, canOpenEditor, debounce } from "../../other/utils/otherUtils.jsx";
import AuthContext from "../context/authContext.jsx";
import {BryntumGrid} from "@bryntum/schedulerpro-react";
import {GRANTS} from "../../other/constants/PrivilegeTypes.jsx";
import {EDITOR_KEYS} from "../../other/constants/EditorKeys.jsx";
import {
  copyTextToClipboard,
  getOriginalRecord,
  swivuCheckGridGrants
} from "../../other/utils/utils.jsx";
import { cellMenuItemRemoveRow, processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import {
  contactGridCellMenuItemRemoveRowHandler,
  CONTACTS_GRID_CONFIG
} from "../../other/constants/ContactViewGridConfig.jsx";
import {STORE_KEYS} from "../../other/constants/StoreKeys.jsx";
import { VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import ViewSpecificHeader from "../components/appHeader/ViewSpecificHeader.jsx";

const ContactPage = () => {
  const gridRef = useRef();
  const { setRefs, isRefsInit } = useRefsInit([gridRef]);
  const { user } = useContext(AuthContext);
  const { project, editorHandler, stateProvider, entityManager } = useContext(bryntumContext);

  useEnableViewTracking(gridRef, VIEW_TYPES.contacts, isRefsInit);

  useEffect(() => {
    // Assign custom project store to the grid
    if (project) {
      const contactGrid = gridRef.current.instance;
      contactGrid.store = project.contactsStore;

      contactGrid.features.cellMenu.items.removeRow = cellMenuItemRemoveRow({
        onItem(e) {
          return contactGridCellMenuItemRemoveRowHandler({ ...e, project });
        }
      });
      swivuCheckGridGrants(contactGrid, user);
      callOnLoadOnColumns(contactGrid.columns, project, { editorHandler });

      const beforeSyncDestroyListener = project.on("beforeSync", (e) => {
        const storeKeysInRequestObj = Object.keys(e.pack);
        const isChangeInContactEntitiesRecords = [
          STORE_KEYS.taskContactStore,
          STORE_KEYS.resourceContactStore,
          STORE_KEYS.clientContactStore,
          STORE_KEYS.userContactStore,
        ].some((storeKey) => storeKeysInRequestObj.includes(storeKey));
        if (isChangeInContactEntitiesRecords) {
          contactGrid.refreshRows();
        }
        return true;
      });

      return () => {
        beforeSyncDestroyListener();
      }
    }
  }, [gridRef, project, user, editorHandler]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
        entityName: "Contact",
        editorHandler,
        grid: gridRef.current.instance,
        user,
        project,
        tagStoreKey: STORE_KEYS.contactTagValuesStore,
      });
      gridRef.current.instance.on("rowExpand", ({ widget }) => {
        assignViewsState([widget], stateProvider);
      }, gridRef.current.instance);
    }
  }, [editorHandler, user]);

  const canOpenElementEditor = debounce(({ event, column, record }) => {
    const originalRecord = getOriginalRecord(record)
    if (!record.isGroupHeader) {
      if ((canOpenEditor(column, originalRecord) && event.detail === 1) && column.type === WIDGET_KEYS.columnContactStatus || column.type === WIDGET_KEYS.columnContactCategory || column.type === WIDGET_KEYS.columnContactTag) {
        editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
          entityRecord: originalRecord,
          event,
          column
        }, { by: "recordType", useExisting: true });
      }
    }
  }, 200);


  const onClickEntity = ({event, column, record}) => {
    const { id, type } = event.target.dataset;
    if (column.id === id && type === 'iconType') {
      copyTextToClipboard(record[id], `Copied ${id} to clipboard`);
    }
    else if (column.id === 'entities') {
      if (type === "Task") {
        const task = project.extraTaskStore.getById(id);
        entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: task.id });
      } else if (type === "Resource") {
        const resource = project.resourceStore.getById(id);
        entityManager.openEntityManager({ entityType: ENTITY_TYPES.RESOURCE, entityId: resource.id });
      } else if (type === "Client") {
        const client = project.clientsStore.getById(id);
        entityManager.openEntityManager({ entityType: ENTITY_TYPES.CLIENT, entityId: client.id });
      } else if (type === "User") {
        const user = project.boardUsersStore.getById(id);
        editorHandler.openEditor(EDITOR_KEYS.userEditor, user, {
          user: user,
          selectTabByTypeOnOpen: "contact"
        });
      }
    } else {
      canOpenElementEditor({ event, column, record });
    }
  }

  return (
    <>
      <ViewSpecificHeader />
      <BryntumGrid
        ref={setRefs(gridRef)}
        {...CONTACTS_GRID_CONFIG}
        stateId
        disabled={user && !user.can(GRANTS.grant_four)}
        readOnly={user && !user.can(GRANTS.grant_four)}
        onCellClick={onClickEntity}
        searchFeature={true}
      />
    </>
  );
};

export default ContactPage;
